import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { decisionPriorityLabel, decisionStatusLabel } from '../decisionFormUtils';
import { FILE_ICONS } from '../data/decisionsData';
import { fetchShareholderDecisionById, submitShareholderDecisionVote } from '../shareholderDecisionsApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './MemberDecisionDetail.css';

const AVATAR_COLORS = ['#02c3a6','#00334a','#02c3a6','#02c3a6','#02c3a6','#c62828','#37474f','#f57c00'];

const STATUS_META = {
  pending: { cls: 'dde__s-pending', icon: 'clock' },
  approved: { cls: 'dde__s-approved', icon: 'check-circle' },
  rejected: { cls: 'dde__s-rejected', icon: 'x-circle' },
};

const PRIORITY_STYLE = {
  high: { color: '#c62828', bg: '#fce4ec', dot: '#e53935' },
  medium: { color: '#e65100', bg: '#fff3e0', dot: '#f57c00' },
  low: { color: '#02c3a6', bg: '#e0f7f4', dot: '#02c3a6' },
};

function daysLeft(deadline) {
  if (!deadline) return null;
  const parts = String(deadline).split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;
  const endOfDeadlineDay = new Date(year, month - 1, day, 23, 59, 59, 999);
  return Math.ceil((endOfDeadlineDay - new Date()) / 86400000);
}

export default function ShareholderDecisionDetail() {
  const { language, direction } = useLanguage();
  const t = useT();

  const voteLabels = useMemo(() => ({
    approve: { label: t('decisions.voteApprove'), cls: 'dde__vote-tag--approve', iconName: 'check-circle', color: '#02c3a6' },
    reject:  { label: t('decisions.voteReject'),  cls: 'dde__vote-tag--reject',  iconName: 'x-circle',     color: '#e53935' },
    abstain: { label: t('decisions.voteAbstain'), cls: 'dde__vote-tag--abstain', iconName: 'clock',        color: '#78909c' },
  }), [t]);

  const { id } = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const row = await fetchShareholderDecisionById(id);
        if (cancelled) return;
        setDecision(row);
        setSelected(row.myVote ?? null);
        setNote(row.myNote ?? '');
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="dde" style={{ padding: 24, color: '#78909c' }}>{t('decisions.loadingDecision')}</div>;

  if (!decision) return (
    <div className="dde" dir={direction}>
      <button type="button" className="dde__back" onClick={() => navigate('/shareholder/decisions')}>
        <Icon name="chevron-right" size={14} /> {t('common.back')}
      </button>
      <div className="dde__not-found">
        <Icon name="decisions" size={52} color="#cfd8dc" strokeWidth={1.2} />
        <p>{loadError || t('decisions.notFound')}</p>
      </div>
    </div>
  );

  const total = decision.totalMembers;
  const votes = decision.votes;
  const approve = votes.filter((v) => v.vote === 'approve').length;
  const reject = votes.filter((v) => v.vote === 'reject').length;
  const abstain = votes.filter((v) => v.vote === 'abstain').length;
  const voted = votes.length;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const dl = daysLeft(decision.deadline);
  const sm = STATUS_META[decision.status] || STATUS_META.pending;
  const prStyle = PRIORITY_STYLE[decision.priority ?? 'medium'] || PRIORITY_STYLE.medium;
  const alreadyVoted = !!decision.myVote;
  const isExpired = dl !== null && dl < 0;
  const ringPct = total ? voted / total : 0;

  const handleSubmit = () => {
    if (!selected) return;
    setSaving(true);
    submitShareholderDecisionVote(decision.id, { choice: selected, notes: note })
      .then((updated) => {
        setDecision(updated);
        setSelected(updated.myVote ?? selected);
        setNote(updated.myNote ?? note);
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : t('decisions.voteFailed'));
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="dde" dir={direction}>

      {loadError && (
        <div style={{ marginBottom: 10, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10, fontSize: '0.88rem' }}>
          {loadError}
        </div>
      )}
      <div className={`dde__hero dde__hero--${decision.status}`}>
        <button type="button" className="dde__back" onClick={() => navigate('/shareholder/decisions')}>
          <Icon name="chevron-right" size={13} /> {t('decisions.backToDecisions')}
        </button>

        <div className="dde__hero-body">
          <div className="dde__hero-left">
            <div className="dde__hero-badges">
              <span className={`dde__status-pill dde__status-pill--${decision.status}`}>
                <Icon name={sm.icon} size={12} /> {decisionStatusLabel(decision.status, language)}
              </span>
              <span className="dde__priority-pill" style={{ color: prStyle.color, background: prStyle.bg }}>
                <span className="dde__priority-dot" style={{ background: prStyle.dot }} />
                {decisionPriorityLabel(decision.priority ?? 'medium', language)}
              </span>
            </div>
            <h1 className="dde__hero-title">{decision.title}</h1>
            <div className="dde__hero-meta">
              <span><Icon name="branch" size={13} />{decision.branch}</span>
              <span><Icon name="user" size={13} />{t('decisions.createdBy')}: {decision.creator}</span>
              <span><Icon name="calendar" size={13} />{decision.date}</span>
              <span className={`dde__deadline${dl !== null && dl <= 2 && dl >= 0 && !alreadyVoted ? ' dde__deadline--urgent' : ''}`}>
                <Icon name="clock" size={13} />
                {dl === null ? t('decisions.noDeadline') : dl >= 0 ? `${dl} ${t('decisions.daysLeft')}` : t('decisions.deadlineEnded')}
              </span>
            </div>
          </div>

          <div className="dde__hero-ring">
            <svg viewBox="0 0 80 80" className="dde__ring-svg">
              <circle cx="40" cy="40" r="34" className="dde__ring-track" />
              <circle cx="40" cy="40" r="34" className="dde__ring-fill"
                strokeDasharray={`${ringPct * 214} 214`} />
            </svg>
            <div className="dde__ring-label">
              <span className="dde__ring-num">{voted}</span>
              <span className="dde__ring-sub">{t('decisions.ringOf')} {total}</span>
              <span className="dde__ring-caption">{t('decisions.ringVoted')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dde__grid">

        <div className="dde__col-main">

          <div className="dde__card">
            <div className="dde__card-head">
              <span className="dde__card-icon dde__card-icon--blue"><Icon name="decisions" size={14} color="#fff" /></span>
              {t('decisions.description')}
            </div>
            <p className="dde__desc">{decision.description}</p>
          </div>

          {decision.attachments?.length > 0 && (
            <div className="dde__card">
              <div className="dde__card-head">
                <span className="dde__card-icon dde__card-icon--indigo"><Icon name="report" size={14} color="#fff" /></span>
                {t('decisions.attachments')}
                <span className="dde__card-badge">{decision.attachments.length}</span>
              </div>
              <div className="dde__attachments">
                {decision.attachments.map((att, i) => {
                  const fi = FILE_ICONS[att.type] ?? FILE_ICONS.pdf;
                  return (
                    <div key={i} className="dde__att">
                      <div className="dde__att-icon" style={{ background: fi.bg }}>
                        <span>{fi.icon}</span>
                      </div>
                      <div className="dde__att-info">
                        <div className="dde__att-name">{att.name}</div>
                        <div className="dde__att-size">{att.size}</div>
                      </div>
                      <button type="button" className="dde__att-btn" title={t('decisions.previewUiOnly')}>
                        <Icon name="eye" size={14} color="#02c3a6" />
                        {t('decisions.preview')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="dde__card">
            <div className="dde__card-head">
              <span className="dde__card-icon dde__card-icon--teal"><Icon name="board" size={14} color="#fff" /></span>
              {t('decisions.votersList')}
              <span className="dde__card-badge">{voted} / {total}</span>
            </div>
            {votes.length === 0 ? (
              <div className="dde__empty-voters">
                <Icon name="user" size={30} color="#e0e0e0" strokeWidth={1.2} />
                <p>{t('decisions.noVotes')}</p>
              </div>
            ) : (
              <div className="dde__voters">
                {votes.map((v, i) => {
                  const vl = voteLabels[v.vote];
                  return (
                    <div key={i} className="dde__voter" style={{ animationDelay: `${i * 0.04}s` }}>
                      <div className="dde__voter-rank">{i + 1}</div>
                      <div className="dde__voter-av" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                        {v.member.charAt(0)}
                      </div>
                      <span className="dde__voter-name">{v.member}</span>
                      <span className={`dde__vote-tag dde__vote-tag--${v.vote}`}>
                        <Icon name={vl.iconName} size={11} /> {vl.label}
                      </span>
                      {v.note && <span className="dde__voter-note">&quot;{v.note}&quot;</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="dde__col-side">

          <div className="dde__card">
            <div className="dde__card-head">
              <span className="dde__card-icon dde__card-icon--purple"><Icon name="chart" size={14} color="#fff" /></span>
              {t('decisions.votingResults')}
            </div>
            <div className="dde__results">
              {[
                { key: 'approve', label: voteLabels.approve.label, count: approve, color: '#02c3a6', track: 'dde__bar--green' },
                { key: 'reject', label: voteLabels.reject.label, count: reject, color: '#e53935', track: 'dde__bar--red' },
                { key: 'abstain', label: voteLabels.abstain.label, count: abstain, color: '#78909c', track: 'dde__bar--grey' },
              ].map((r) => (
                <div key={r.key} className="dde__result-row">
                  <div className="dde__result-top">
                    <span className="dde__result-label" style={{ color: r.color }}>
                      <Icon name={voteLabels[r.key].iconName} size={12} color={r.color} />
                      {r.label}
                    </span>
                    <span className="dde__result-count" style={{ color: r.color }}>
                      {r.count} <span className="dde__result-pct">({pct(r.count)}%)</span>
                    </span>
                  </div>
                  <div className="dde__result-track">
                    <div className={`dde__result-bar ${r.track}`} style={{ width: `${pct(r.count)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="dde__result-summary">
              <div className="dde__result-summary-num">{voted} / {total}</div>
              <div className="dde__result-summary-lbl">{t('decisions.membersVoted')}</div>
              <div className="dde__result-summary-remain">
                <Icon name="clock" size={11} /> {Math.max(0, total - voted)} {t('decisions.notVotedYetCount')}
              </div>
            </div>
          </div>

          <div className="dde__vote-card">
            <div className="dde__vote-card-head">
              <span className="dde__card-icon dde__card-icon--green"><Icon name="voting" size={14} color="#fff" /></span>
              {alreadyVoted ? t('decisions.yourVote') : t('decisions.castVote')}
            </div>

            {alreadyVoted ? (
              <div className="dde__voted-state">
                <div className={`dde__voted-banner dde__vote-tag--${decision.myVote}`}>
                  <Icon name="check-circle" size={20} />
                  <div>
                    <div className="dde__voted-banner-lbl">{t('decisions.youVoted')}</div>
                    <div className="dde__voted-banner-val">{voteLabels[decision.myVote].label}</div>
                  </div>
                </div>
                {decision.myNote && (
                  <div className="dde__voted-note">
                    <Icon name="decisions" size={12} color="#90a4ae" />
                    <span>&quot;{decision.myNote}&quot;</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="dde__voting">
                <p className="dde__vote-prompt">{t('decisions.chooseOpinion')}</p>
                <div className="dde__vote-btns">
                  {[
                    { key: 'approve', label: voteLabels.approve.label, icon: 'check-circle', color: '#02c3a6' },
                    { key: 'reject', label: voteLabels.reject.label, icon: 'x-circle', color: '#c62828' },
                    { key: 'abstain', label: voteLabels.abstain.label, icon: 'clock', color: '#546e7a' },
                  ].map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      disabled={isExpired}
                      onClick={() => setSelected(selected === v.key ? null : v.key)}
                      className={`dde__vbtn dde__vbtn--${v.key}${selected === v.key ? ' dde__vbtn--sel' : ''}`}
                    >
                      <Icon name={v.icon} size={24} color={selected === v.key ? '#fff' : v.color} strokeWidth={1.8} />
                      <span>{v.label}</span>
                    </button>
                  ))}
                </div>

                {selected && (
                  <div className="dde__note-wrap">
                    <label className="dde__note-label">
                      <Icon name="decisions" size={12} color="#90a4ae" /> {t('decisions.noteOptional')}
                    </label>
                    <textarea
                      className="dde__note-area"
                      rows={3}
                      placeholder={t('decisions.notePlaceholder')}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <button type="button" className="dde__submit" onClick={handleSubmit} disabled={saving}>
                      {saving
                        ? <><span className="dde__spinner" /> {t('decisions.sending')}</>
                        : <><Icon name="check" size={15} color="#fff" /> {t('decisions.sendVote')}</>}
                    </button>
                  </div>
                )}

                {isExpired && (
                  <div className="dde__expired">
                    <Icon name="clock" size={14} color="#e53935" />
                    {t('decisions.voteClosed')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
